export type LinkKind = "web" | "email" | "predefined" | "attribute";

export const LinkKindOptions: { Key: LinkKind; Value: string }[] = [
  { Key: "web", Value: "Web Address" },
  { Key: "email", Value: "Email Address" },
  { Key: "predefined", Value: "Predefined" },
  { Key: "attribute", Value: "Attribute" },
];

/** The portal's own links, which the email service fills in for each recipient. */
export const PredefinedLinks: { Key: string; Value: string }[] = [
  { Key: "actions/redirect.aspx?url=../home.html", Value: "View Email In Browser" },
  {
    Key: "/actions/portal.aspx?type=temporaryaccess&email=$$attribute:emailaddress1-$&token=$$attribute:g4_accesstoken-$",
    Value: "Temporary Access",
  },
  {
    Key: "/actions/portal.aspx?type=update&email=$$attribute:emailaddress1-$",
    Value: "Update Details",
  },
  { Key: "/actions/portal.aspx?type=unsubscribe", Value: "Unsubscribe" },
];

export interface LinkValues {
  kind: LinkKind;
  url: string;
  emailAddress: string;
  emailSubject: string;
  emailContent: string;
  predefined: string;
  attribute: string;
  text: string;
  newTab: boolean;
}

export const emptyLink: LinkValues = {
  kind: "web",
  url: "",
  emailAddress: "",
  emailSubject: "",
  emailContent: "",
  predefined: "",
  attribute: "",
  text: "",
  newTab: false,
};

const AttributeLink = /^actions\/redirect\.aspx\?url=\$\$attribute:([^-$]*)-\$/;

export function buildHref(link: LinkValues) {
  switch (link.kind) {
    case "email": {
      const params = [
        link.emailSubject && `subject=${encodeURIComponent(link.emailSubject)}`,
        link.emailContent && `body=${encodeURIComponent(link.emailContent)}`,
      ].filter(Boolean);

      return `mailto:${link.emailAddress}${params.length ? `?${params.join("&")}` : ""}`;
    }
    case "predefined":
      return link.predefined;
    case "attribute":
      return `actions/redirect.aspx?url=$$attribute:${link.attribute}-$`;
    default:
      return link.url;
  }
}

/** What an existing link's address says it is, for editing it. */
export function parseHref(href: string): Partial<LinkValues> {
  if (PredefinedLinks.some((link) => link.Key === href)) {
    return { kind: "predefined", predefined: href };
  }

  const email = /^mailto:([^?]*)/.exec(href);

  if (email) {
    const params = new URLSearchParams(href.split("?")[1] ?? "");

    return {
      kind: "email",
      emailAddress: email[1],
      emailSubject: params.get("subject") ?? "",
      emailContent: params.get("body") ?? "",
    };
  }

  const attribute = AttributeLink.exec(href);

  return attribute ? { kind: "attribute", attribute: attribute[1] } : { kind: "web", url: href };
}

/** What is wrong with the link, per field, as the legacy dialog checked. */
export function validateLink(link: LinkValues): Partial<Record<keyof LinkValues, string>> {
  const errors: Partial<Record<keyof LinkValues, string>> = {};
  const required: Record<LinkKind, keyof LinkValues> = {
    web: "url",
    email: "emailAddress",
    predefined: "predefined",
    attribute: "attribute",
  };

  if (!link[required[link.kind]]) {
    errors[required[link.kind]] = "Required";
  }

  if (!link.text) {
    errors.text = "Required";
  }

  return errors;
}
